import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Copy, Check, ExternalLink } from 'lucide-react';
import { getStore, getCoupons } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from '../hooks/use-toast';

const StorePage = () => {
  const { id } = useParams();
  const [copiedCode, setCopiedCode] = useState(null);
  const [store, setStore] = useState(null);
  const [storeCoupons, setStoreCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStoreData();
  }, [id]);

  const loadStoreData = async () => {
    setLoading(true);
    try {
      const [storeData, couponsData] = await Promise.all([
        getStore(id),
        getCoupons({ store_id: id })
      ]);
      setStore(storeData);
      setStoreCoupons(couponsData);
    } catch (error) {
      console.error('Error loading store data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading store...</p>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Store not found</h2>
          <Link to="/">
            <Button className="bg-slate-900 hover:bg-slate-800 text-white">
              Go Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleCopyCode = (code, couponId, storeDomain) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(couponId);
    toast({
      title: "Code Copied!",
      description: `Coupon code "${code}" copied to clipboard`,
    });
    // Open store website in new tab
    window.open(`https://${storeDomain}`, '_blank');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      <Header />

      {/* Store Header */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center text-slate-700 hover:text-slate-900 mb-6 font-medium">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-start space-x-6">
            <div className="w-24 h-24 bg-white rounded-2xl shadow-lg flex items-center justify-center border border-slate-200">
              <img 
                src={store.logo} 
                alt={store.name} 
                className="w-20 h-20 object-contain" 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(store.name)}&background=1e293b&color=fff&size=128`;
                }} 
              />
            </div>
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-slate-900 mb-2">{store.name}</h1>
              <p className="text-lg text-slate-600 mb-4">{store.description}</p>
              <div className="flex items-center space-x-4">
                <Badge className="bg-slate-900 text-white">
                  {storeCoupons.length} Active Coupons
                </Badge>
                <a
                  href={`https://${store.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-slate-700 hover:text-slate-900 font-medium"
                >
                  Visit {store.name}
                  <ExternalLink className="w-4 h-4 ml-1" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coupons List */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">Available Coupons & Deals</h2>
          {storeCoupons.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <p className="text-slate-600">No active coupons available for this store at the moment.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {storeCoupons.map(coupon => (
                <Card key={coupon.id} className="hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-slate-300">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <Badge className="bg-slate-900 text-white font-bold text-lg px-4 py-1">
                            {coupon.discount}
                          </Badge>
                          {coupon.verified && (
                            <Badge variant="outline" className="border-green-500 text-green-700">
                              Verified
                            </Badge>
                          )}
                          <Badge variant="outline" className="border-slate-300 text-slate-700">
                            {coupon.category}
                          </Badge>
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2">{coupon.title}</h3>
                        <p className="text-slate-600 mb-3">{coupon.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-slate-500">
                          {coupon.expiry_date && (
                            <span>Expires: {new Date(coupon.expiry_date).toLocaleDateString()}</span>
                          )}
                          {coupon.savings && (
                            <span className="text-green-600 font-medium">Users saved {coupon.savings}</span>
                          )}
                        </div>
                      </div>
                      <div className="md:ml-6">
                        {coupon.code ? (
                          <div className="space-y-2">
                            <div className="bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg px-6 py-3 text-center">
                              <span className="text-lg font-mono font-bold text-slate-900">{coupon.code}</span>
                            </div>
                            <Button
                              onClick={() => handleCopyCode(coupon.code, coupon.id, store.domain)}
                              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                            >
                              {copiedCode === coupon.id ? (
                                <><Check className="w-4 h-4 mr-2" /> Copied!</>
                              ) : (
                                <><Copy className="w-4 h-4 mr-2" /> Copy Code</>
                              )}
                            </Button>
                          </div>
                        ) : (
                          <Button 
                            onClick={() => {
                              toast({
                                title: "Opening Deal!",
                                description: `Redirecting to ${store.name} for this exclusive deal`,
                              });
                              window.open(`https://${store.domain}`, '_blank');
                            }}
                            className="bg-slate-900 hover:bg-slate-800 text-white px-8"
                          >
                            Get Deal
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StorePage;
